import { FileOutlined } from '@ant-design/icons'
import { BBDTag, GraviButton, Horizontal, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'

import { Popover, Switch, theme } from 'antd'
import dayjs from 'dayjs'
import type * as React from 'react'

import type { AutomaticAccessorial, ServiceRule } from '../../../../../../api/useFreight/types'
import { isPercentageFeeType } from '../../../../shared/util/feeTypeUtils'
import { ActiveStatusRenderer, type BaseRate } from '../../components/ActiveStatusRenderer'

// Add interface for product details
interface ProductDetails {
  categories: string[]
  products: Record<string, { id: string; name: string }[]>
}

// Helper function to convert snake_case to Title Case
const toTitleCase = (str: string) => {
  if (!str) return ''
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export function ActiveRenderer({
  value,
  data,
}: {
  value: boolean
  data: AutomaticAccessorial
}) {
  return <ActiveStatusRenderer value={value} data={data as BaseRate} rateType='automaticAccessorial' />
}

export function LocationRenderer({ value }: { value: string }) {
  if (!value) {
    return <Texto appearance='hint'>Any</Texto>
  }
  return <Texto>{value}</Texto>
}

export function RegionRenderer({ value }: { value: string | undefined }) {
  if (!value) {
    return <Texto appearance='hint'>Any</Texto>
  }

  // Convert snake_case to Title Case
  const formattedValue = toTitleCase(value)

  return <BBDTag theme1>{formattedValue}</BBDTag>
}

export function ProductGroupRenderer({
  data,
}: {
  value?: string
  data: AutomaticAccessorial
}) {
  // Check if a specific product group is defined
  if (data?.product_group) {
    return (
      <BBDTag theme1 style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        {data.product_group}
      </BBDTag>
    )
  }

  // Check if specific product IDs are defined (assuming field name is product_ids)
  // Adjust the field name `product_ids` if it differs in the actual AutomaticAccessorial type
  const productIds = data?.product_ids
  if (Array.isArray(productIds) && productIds.length > 0) {
    return (
      <BBDTag theme2 style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        {`${productIds.length} Products`}
      </BBDTag>
    )
  }

  // Default case: Neither product group nor specific products are defined
  return <Texto appearance='hint'>Any</Texto>
}

export function FeeTypeRenderer({ value }: { value: string }) {
  if (!value) {
    return <Texto appearance='hint'>N/A</Texto>
  }

  // Define icons for each fee type
  const feeTypeIcons: Record<string, React.ReactNode> = {
    'Per Order': <FileOutlined />,
    'Per Order Load': <MaterialIcon icon='upload' />,
    'Per Order Drop': <MaterialIcon icon='download' />,
    'Per Order Stop': <MaterialIcon icon='location_on' />,
    'Per Order Mile': <MaterialIcon icon='route' />,
    'Per Order Hour': <MaterialIcon icon='schedule' />,
    'Per Order Gallon': <MaterialIcon icon='local_gas_station' />,
    'Base Freight %': <MaterialIcon icon='percent' />,
    'Base & Accessorial %': <MaterialIcon icon='percent' />,
    'Total Freight %': <MaterialIcon icon='percent' />,
    'Base & Surcharge %': <MaterialIcon icon='percent' />,
    'Surcharge %': <MaterialIcon icon='percent' />,
    'Per Event': <MaterialIcon icon='event' />,
    'Per Stop': <MaterialIcon icon='location_on' />,
    'Per Drop': <MaterialIcon icon='download' />,
    'Per Load': <MaterialIcon icon='upload' />,
    'Per No Load': <MaterialIcon icon='file_upload_off' />,
    'Per Mile': <MaterialIcon icon='route' />,
    'Per Hour': <MaterialIcon icon='schedule' />,
    'Per Volume': <MaterialIcon icon='local_gas_station' />,
    'Per Trigger': <MaterialIcon icon='bolt' />,
    'Above Min': <MaterialIcon icon='trending_up' />,
  }

  // Get the icon based on the fee type, or use a default
  const icon = feeTypeIcons[value] || <MaterialIcon icon='attach_money' />

  // Handle the display text based on fee type
  let displayText: { prefix?: string; main: string; showPercent?: boolean } = {
    main: value,
  }

  if (value.startsWith('Per ')) {
    displayText = {
      prefix: 'Per',
      main: value.substring(4),
    }
  } else if (value === 'Base Freight %') {
    displayText = {
      prefix: 'Base',
      main: 'Freight',
      showPercent: false,
    }
  } else if (value === 'Base & Accessorial %') {
    displayText = {
      prefix: 'Base',
      main: '& Accessorial',
      showPercent: false,
    }
  } else if (value === 'Total Freight %') {
    displayText = {
      main: 'Total',
      showPercent: false,
    }
  }

  return (
    <Horizontal gap={8} verticalCenter>
      {icon}
      <Horizontal gap={2}>
        {displayText.prefix && <Texto>{displayText.prefix}</Texto>}
        <Texto weight='bold'>{displayText.main}</Texto>
        {displayText.showPercent && (
          <Texto category='label' appearance='hint'>
            %
          </Texto>
        )}
      </Horizontal>
    </Horizontal>
  )
}

export function RateRenderer({
  value,
  data,
}: {
  value: number | null
  data: AutomaticAccessorial
}) {
  // Return N/A if the rate is null
  if (value === null || value === undefined) {
    return (
      <Texto appearance='hint' style={{ textAlign: 'right', width: '100%' }}>
        N/A
      </Texto>
    )
  }

  const feeType = data.fee_type?.type || ''
  const isPercentageType = isPercentageFeeType(feeType)

  if (isPercentageType) {
    // For percentage types, the API stores as decimal (0.20 for 20%)
    // So we multiply by 100 to display as percentage
    const percentValue = (value * 100).toFixed(3)
    return (
      <Texto weight='bold' style={{ textAlign: 'right', width: '100%' }}>
        {percentValue}%
      </Texto>
    )
  }

  // For non-percentage types, display as currency
  return (
    <Texto weight='bold' style={{ textAlign: 'right', width: '100%' }}>
      ${value.toFixed(5)}
    </Texto>
  )
}

export function NextRateRenderer({ value }: { value: string | null }) {
  if (!value) {
    return <Texto appearance='hint'>N/A</Texto>
  }
  return <Texto>{dayjs(value).format('MM/DD/YYYY')}</Texto>
}

export function ApprovalRenderer({ value }: { value: boolean }) {
  const { token } = theme.useToken()
  return <Switch checked={value} style={{ backgroundColor: value ? token.colorWarning : '' }} />
}

export function ExcludeFromInvoiceRenderer({ value }: { value: boolean }) {
  const { token } = theme.useToken()
  return <Switch checked={value} style={{ backgroundColor: value ? token.colorPrimary : '' }} />
}

export function TriggerRenderer({ rule }: { rule?: ServiceRule }) {
  if (!rule) {
    return null
  }

  // Determine the icon based on rule type
  let icon = null
  if (rule.type === 'Holiday') {
    icon = <MaterialIcon icon='celebration' style={{ marginRight: 6 }} />
  } else if (rule.type === 'Weekend') {
    icon = <MaterialIcon icon='weekend' style={{ marginRight: 6 }} />
  }

  return (
    <Vertical gap={8} style={{ width: '100%' }}>
      {!rule.threshold_type ? (
        <BBDTag
          key={rule.type}
          theme2
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
          {rule.type}
        </BBDTag>
      ) : (
        <Horizontal key={rule.type} style={{ width: '100%' }}>
          <BBDTag
            theme2
            style={{
              flex: 1,
              justifyContent: 'center',
              minWidth: '100px',
            }}
          >
            {rule.type}
          </BBDTag>
        </Horizontal>
      )}
    </Vertical>
  )
}

export function LocationHierarchyRenderer({
  data,
  type,
}: {
  data: AutomaticAccessorial
  type: 'origins' | 'destinations'
}) {
  if (!data?.[type]) return <Texto appearance='hint'>Any</Texto>

  const siteNames = data[type]?.locations.map((site) => site.name)
  const siteStates = data[type]?.site_states
  const terminalStates = data[type]?.terminal_states
  const siteRegions = data[type]?.site_regions

  const content = (
    <Vertical style={{ gap: '1rem', maxWidth: '180px' }}>
      <Texto category='h5' weight='bold'>
        Locations
      </Texto>
      <Vertical gap={16}>
        {siteStates?.length > 0 && (
          <Horizontal gap={16}>
            <Texto weight='bold'>Site States:</Texto>
            <Vertical>
              {siteStates.map((name) => (
                <Texto align='right' key={name}>
                  {name}
                </Texto>
              ))}
            </Vertical>
          </Horizontal>
        )}
        {terminalStates?.length > 0 && (
          <Horizontal gap={16}>
            <Texto weight='bold'>Terminal States:</Texto>
            <Vertical>
              {terminalStates.map((name) => (
                <Texto align='right' key={name}>
                  {name}
                </Texto>
              ))}
            </Vertical>
          </Horizontal>
        )}
        {siteRegions?.length > 0 && (
          <Horizontal gap={16}>
            <Texto weight='bold'>Site Regions:</Texto>
            <Vertical>
              {siteRegions.map((region) => (
                <Texto align='right' key={region.id}>
                  {region.name}
                </Texto>
              ))}
            </Vertical>
          </Horizontal>
        )}

        {siteNames?.length > 0 && (
          <Horizontal gap={16}>
            <Texto weight='bold'>Locations:</Texto>
            <Vertical>
              {siteNames.map((name) => (
                <Texto align='right' key={name}>
                  {name}
                </Texto>
              ))}
            </Vertical>
          </Horizontal>
        )}
      </Vertical>
    </Vertical>
  )

  const selectedCount = type === 'origins' ? data.origin_ids?.length || 0 : data.destination_ids?.length || 0

  return (
    <Popover content={content} placement='bottomLeft'>
      <BBDTag theme2>{selectedCount} Selected</BBDTag>
    </Popover>
  )
}

export function NameRenderer({ value }: { value: string }) {
  return <Texto weight='bold'>{value}</Texto>
}

export function MinimalistActiveRenderer({
  value,
  onChange,
}: {
  value: boolean
  onChange: (value: boolean) => void
}) {
  const { token } = theme.useToken()

  return (
    <Switch
      checked={!value}
      onChange={(checked) => onChange(checked)}
      style={{ backgroundColor: !value ? token.colorSuccess : '' }}
      checkedChildren='Active'
      unCheckedChildren='Inactive'
    />
  )
}
